import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Coins, Wallet, MapPin, Heart, QrCode, Smartphone, Gift, CheckCircle2, ArrowRight, Star, ShoppingBag, Compass, Sparkles } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1556742044-3c52d6e88c62?w=1600&q=80";

export default function LandingForCustomers() {
  const [segments, setSegments] = useState([]);
  useEffect(() => {
    api.get("/segments?active=true").then((r) => setSegments(r.data || [])).catch(() => {});
  }, []);
  return (
    <div className="min-h-[calc(100vh-4rem)] bg-stone-50">
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO} alt="" className="w-full h-full object-cover"/>
          <div className="absolute inset-0 bg-gradient-to-br from-blue-950/95 via-blue-950/85 to-blue-900/70"/>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-24 text-white">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-400/20 border border-emerald-400/40 text-emerald-300 text-xs font-bold uppercase tracking-widest" data-testid="customer-tag">
            <Coins size={12}/> Para clientes
          </span>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold mt-4 leading-tight max-w-3xl">
            Compre perto de casa<br/>e <span className="text-emerald-400">ganhe dinheiro de volta</span> em cada compra.
          </h1>
          <p className="text-base sm:text-lg text-blue-100 mt-5 max-w-2xl">
            Um cashback que vale em qualquer estabelecimento parceiro. Acumule, junte tudo numa única carteira global, e use onde você quiser na cidade. Simples assim.
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <Link to="/register" data-testid="cta-register-customer">
              <Button size="lg" className="bg-emerald-500 hover:bg-emerald-600 text-white font-bold rounded-xl shadow-lg shadow-emerald-900/30 px-6 h-12">
                Criar conta grátis <ArrowRight size={16} className="ml-1"/>
              </Button>
            </Link>
            <Link to="/" data-testid="cta-explore-map">
              <Button size="lg" variant="outline" className="bg-white/10 hover:bg-white/20 text-white border-white/30 rounded-xl px-6 h-12">
                Explorar o mapa
              </Button>
            </Link>
          </div>
          <div className="flex flex-wrap gap-5 mt-8 text-xs text-blue-100">
            <span className="inline-flex items-center gap-1.5"><CheckCircle2 size={14}/> 100% grátis</span>
            <span className="inline-flex items-center gap-1.5"><CheckCircle2 size={14}/> Sem taxa pro cliente</span>
            <span className="inline-flex items-center gap-1.5"><CheckCircle2 size={14}/> Acumula sem expirar</span>
          </div>
        </div>
      </section>

      {/* DESCOBRIR POR SEGUIMENTOS */}
      <section className="relative bg-gradient-to-b from-stone-50 to-white py-16 sm:py-20 border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-10">
            <div className="max-w-2xl">
              <span className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.25em] text-blue-900 font-bold">
                <Sparkles size={14}/> Descobrir por Seguimentos
              </span>
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-stone-900 mt-3">Tudo que você procura, organizado do seu jeito.</h2>
              <p className="text-stone-600 mt-3 text-sm sm:text-base">
                Onde Comer Bem, Tudo pra sua Casa, Saúde e Bem-estar, Lazer, Eventos…
                Escolha o que precisa e a gente mostra <strong>no mapa</strong>, perto de você,
                com os melhores parceiros e <span className="text-emerald-700 font-bold">cashback ativo</span>.
              </p>
            </div>
            <Link to="/discover/all" data-testid="cta-segments-discover">
              <Button size="lg" className="bg-blue-900 hover:bg-blue-950 text-white rounded-xl px-5 h-12">
                <Compass size={16} className="mr-1.5"/> Começar a descobrir
              </Button>
            </Link>
          </div>
          {segments.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="customer-segments-grid">
              {segments.slice(0, 6).map((s) => (
                <Link key={s.slug} to={`/discover/all?segment=${s.slug}`} className="group relative rounded-2xl overflow-hidden border border-stone-200 hover:border-amber-400 hover:shadow-xl transition-all" data-testid={`cust-seg-${s.slug}`}>
                  <div className="aspect-[4/5] bg-gradient-to-br from-amber-100 to-blue-100">
                    {s.cover_url ? (
                      <img src={s.cover_url} alt={s.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"/>
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-stone-300 font-display text-4xl">{s.name[0]}</div>
                    )}
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-blue-950/95 via-blue-950/30 to-transparent flex flex-col justify-end p-3">
                    <span className="text-white font-display font-bold text-sm sm:text-base leading-tight">{s.cta || s.name}</span>
                    <span className="text-blue-100 text-[10px] mt-1 line-clamp-2">{s.description}</span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
        <div className="text-center mb-12">
          <span className="text-xs uppercase tracking-[0.25em] text-emerald-700 font-bold">Como funciona</span>
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-stone-900 mt-3">3 passos pra começar a economizar</h2>
        </div>
        <div className="grid sm:grid-cols-3 gap-5">
          {[
            { icon: Smartphone, n: "01", t: "Crie sua conta", d: "Em segundos, no app. Você recebe um código único de carteira (MB-XXXX-XXXX)." },
            { icon: QrCode, n: "02", t: "Mostre o QR ou o código", d: "Na hora de pagar, mostre seu QR Code ou diga seu código pro caixa. Pronto." },
            { icon: Wallet, n: "03", t: "Use onde quiser", d: "Saldo acumulado vale em qualquer parceiro Prata ou Ouro do seu cashback global." },
          ].map((s, i) => (
            <div key={i} className="relative p-6 rounded-3xl bg-white border border-stone-200 hover:-translate-y-1 hover:shadow-xl transition-all" data-testid={`how-${i}`}>
              <div className="absolute -top-4 left-6 w-10 h-10 rounded-2xl bg-emerald-500 text-white flex items-center justify-center font-display font-bold">{s.n}</div>
              <div className="w-12 h-12 rounded-2xl bg-emerald-50 text-emerald-700 flex items-center justify-center mt-2 mb-3"><s.icon size={22}/></div>
              <h3 className="font-display text-xl font-semibold text-stone-900">{s.t}</h3>
              <p className="text-sm text-stone-600 mt-2">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FEATURES */}
      <section className="bg-white py-16 sm:py-20 border-y border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-xs uppercase tracking-[0.25em] text-emerald-700 font-bold">Pensado pra você</span>
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-stone-900 mt-3">Tudo o que você precisa numa carteira só</h2>
              <ul className="mt-6 space-y-4">
                {[
                  { icon: Coins, t: "Cashback global", d: "Acumule cashback em todas as suas compras na sua cidade — tudo num único saldo." },
                  { icon: Heart, t: "Favoritos", d: "Marque seus estabelecimentos preferidos e tenha eles sempre no topo das buscas." },
                  { icon: Star, t: "Avaliações", d: "Avalie restaurantes, lojas e serviços. Ajude outras pessoas a escolher melhor." },
                  { icon: ShoppingBag, t: "Aprovação no app", d: "Resgate seguro: o comerciante envia o pedido, você aprova antes do desconto." },
                  { icon: MapPin, t: "Mapa interativo", d: "Encontre por categoria, por proximidade ou pelo que aceita cashback." },
                ].map((f, i) => (
                  <li key={i} className="flex gap-4" data-testid={`feature-${i}`}>
                    <div className="w-10 h-10 shrink-0 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center"><f.icon size={18}/></div>
                    <div>
                      <h4 className="font-display font-semibold text-stone-900">{f.t}</h4>
                      <p className="text-sm text-stone-600 mt-0.5">{f.d}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
            <div className="relative">
              <div className="rounded-3xl bg-gradient-to-br from-blue-900 to-blue-950 p-8 text-white shadow-2xl border-4 border-amber-400 transform lg:-rotate-1">
                <span className="text-[10px] uppercase tracking-widest text-blue-200 font-bold">Seu saldo</span>
                <div className="font-display text-5xl font-bold mt-1">R$ 127,40</div>
                <p className="text-sm text-blue-200 mt-1">Sua carteira global · Maria S.</p>
                <div className="mt-5 p-4 rounded-2xl bg-white text-stone-900 text-center">
                  <div className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">Código da carteira</div>
                  <div className="font-mono text-xl font-extrabold text-blue-950 mt-1 tracking-wider">MB-7K2P-9XA4</div>
                  <div className="w-32 h-32 mx-auto mt-3 bg-stone-100 rounded-xl flex items-center justify-center"><QrCode size={64} className="text-blue-950"/></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { n: "5%", l: "Cashback típico em parceiros" },
            { n: "0", l: "Taxa pro cliente" },
            { n: "1", l: "Carteira global pra tudo" },
            { n: "∞", l: "Saldo acumulado sem expirar" },
          ].map((s, i) => (
            <div key={i} className="text-center p-6 rounded-2xl bg-emerald-50 border border-emerald-200" data-testid={`stat-${i}`}>
              <div className="font-display text-5xl font-bold text-emerald-700">{s.n}</div>
              <div className="text-xs uppercase tracking-wider text-emerald-900 font-bold mt-2">{s.l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* FOOTER CTA */}
      <section className="bg-gradient-to-r from-emerald-500 to-emerald-600 text-white py-12">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 text-center">
          <Gift size={42} className="mx-auto mb-4"/>
          <h3 className="font-display text-2xl sm:text-3xl font-bold">Sua próxima compra pode te devolver dinheiro.</h3>
          <p className="mt-2 text-sm sm:text-base">Crie sua conta agora — em menos de 30 segundos.</p>
          <Link to="/register" data-testid="cta-register-customer-2">
            <Button size="lg" className="mt-5 bg-white hover:bg-stone-100 text-emerald-700 font-bold rounded-xl px-8 h-12">
              Quero meu cashback <ArrowRight size={16} className="ml-1"/>
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
